import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Bridge screen between [กดดำเนินการสั่งซื้อ] and Shopify checkout.
 * Shows a calming loading UI so the user doesn't see a flash of empty cart
 * (per Line CRM Feedback: "ก่อนระบบการสั่งซื้อแว๊บหนึ่งมันจะโชว์หน้าตระกร้าที่ว่างเปล่า")
 */
interface RedirectState {
  checkoutUrl?: string;
  total?: number;
  itemCount?: number;
}

export function CheckoutRedirect() {
  const { state } = useLocation() as { state: RedirectState | null };
  const navigate = useNavigate();
  const [counter, setCounter] = useState(3);

  useEffect(() => {
    // No URL → user landed here by mistake, send back to cart
    if (!state?.checkoutUrl) {
      navigate('/shop/cart', { replace: true });
      return;
    }

    const url = state.checkoutUrl;

    // Visible countdown 3 → 0, then redirect
    const tick = setInterval(() => setCounter((c) => Math.max(0, c - 1)), 700);
    const go = setTimeout(() => {
      window.location.replace(url);
    }, 2_100);

    return () => { clearInterval(tick); clearTimeout(go); };
  }, [state, navigate]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center
                    bg-gradient-to-br from-gray-900 to-black text-white px-6">
      <div className="text-center max-w-sm w-full">
        {/* Brand */}
        <div className="text-2xl font-bold tracking-tight mb-1">mesoestetic</div>
        <div className="text-xs text-white/50 tracking-wide mb-12">CRM Rewards</div>

        {/* Animated dots / spinner */}
        <div className="relative w-20 h-20 mx-auto mb-8">
          <div className="absolute inset-0 rounded-full border-2 border-white/10" />
          <div className="absolute inset-0 rounded-full border-2 border-t-white animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                 className="w-9 h-9 text-white">
              <path strokeLinecap="round" strokeLinejoin="round"
                    d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
            </svg>
          </div>
        </div>

        <div className="text-lg font-semibold mb-2">กำลังเตรียมหน้าชำระเงิน</div>
        <div className="text-sm text-white/60 leading-relaxed mb-6">
          ระบบกำลัง auto-login เข้า Shopify ให้อัตโนมัติ<br />
          กรุณารอสักครู่...
        </div>

        {/* Order summary chip */}
        {state?.total != null && (
          <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm
                          border border-white/15 rounded-full px-4 py-2 text-sm">
            {state.itemCount != null && (
              <span className="text-white/70">{state.itemCount} ชิ้น</span>
            )}
            <span className="w-px h-3 bg-white/20" />
            <span className="font-bold">
              ฿{state.total.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
            </span>
          </div>
        )}

        <div className="text-[11px] text-white/40 mt-10">
          จะ redirect ใน {counter} วินาที — ห้ามปิดหน้าต่าง
        </div>
      </div>
    </div>
  );
}
